// Content for "Comment ATNA crée cette capacité" — ATNA page, section 2.
export type Bi = { fr: string; en: string }
export type StepKey = 'identify' | 'context' | 'understand' | 'verify' | 'recommend' | 'act'
export type SourceKey = 'bylaw' | 'territory' | 'internal' | 'official'

export const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png'
export const USER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/8604a75f-fffa-41a4-b6a9-101ab8fd41cb.jpg'

export const atnaHowCopy = {
  eyebrow: { fr: 'Comment ATNA crée cette capacité', en: 'How ATNA creates this capacity' },
  headline: [
    { text: { fr: 'De l’intention à l’action.', en: 'From intent to action.' }, accent: false },
    { text: { fr: 'Sans perdre le contexte.', en: 'Without losing context.' }, accent: true },
  ],
  body: {
    fr: 'ATNA comprend ce que vos usagers demandent, utilise le contexte autorisé de votre organisation, vérifie l’information et orchestre les prochaines actions — pour offrir un service plus rapide et libérer la capacité de vos équipes.',
    en: "ATNA understands what your users are asking, uses your organization's authorized context, verifies the information and orchestrates the next actions — to deliver faster service and free up your teams' capacity.",
  },
  intent: { fr: 'Je veux installer une piscine.', en: 'I want to install a pool.' },
  coreLabel: [
    { fr: 'Intelligence', en: 'Agentic' },
    { fr: 'agentique', en: 'intelligence' },
  ],
  trust: {
    lead: { fr: 'ATNA ne devine pas.', en: "ATNA doesn't guess." },
    accent: [{ fr: 'Elle vérifie avant d’agir.', en: 'It verifies before acting.' }],
  },
  chatbotTitle: { fr: 'Robot conversationnel classique', en: 'Classic AI chatbot' },
  atnaTitle: { fr: 'ATNA — Intelligence opérationnelle', en: 'ATNA — Operational intelligence' },
  tameTitle: { fr: 'Niveau de confiance TAME', en: 'TAME confidence level' },
  tameCaption: { fr: 'Confiance pour cette réponse · Exemple illustratif', en: 'Confidence for this answer · Illustrative example' },
  tameFooter: { fr: 'Sources vérifiées · Traçabilité active', en: 'Sources verified · Traceability active' },
}

export const atnaSteps: { key: StepKey; label: Bi; lines: Bi[]; status: Bi }[] = [
  {
    key: 'identify',
    label: { fr: 'Identifie', en: 'Identifies' },
    lines: [{ fr: 'Marie Tremblay', en: 'Marie Tremblay' }, { fr: 'Identité vérifiée', en: 'Verified identity' }],
    status: { fr: 'Vérifié', en: 'Verified' },
  },
  {
    key: 'context',
    label: { fr: 'Contextualise', en: 'Contextualizes' },
    lines: [{ fr: '123 rue Principale', en: '123 Principale St.' }, { fr: 'Propriété reconnue', en: 'Property recognized' }],
    status: { fr: 'Propriété validée', en: 'Property validated' },
  },
  {
    key: 'understand',
    label: { fr: 'Comprend', en: 'Understands' },
    lines: [{ fr: 'Installation d’une', en: 'Installing a' }, { fr: 'piscine résidentielle', en: 'residential pool' }],
    status: { fr: 'Intention confirmée', en: 'Intent confirmed' },
  },
  {
    key: 'verify',
    label: { fr: 'Vérifie', en: 'Verifies' },
    lines: [
      { fr: 'Règlements municipaux', en: 'Municipal by-laws' },
      { fr: '+ territoire + sources', en: '+ territory + official' },
      { fr: 'officielles', en: 'sources' },
    ],
    status: { fr: 'Sources vérifiées', en: 'Sources verified' },
  },
  {
    key: 'recommend',
    label: { fr: 'Recommande', en: 'Recommends' },
    lines: [{ fr: 'Permis requis', en: 'Permit required' }, { fr: 'Documents nécessaires', en: 'Required documents' }],
    status: { fr: 'Confiance élevée', en: 'High confidence' },
  },
  {
    key: 'act',
    label: { fr: 'Agit', en: 'Acts' },
    lines: [{ fr: 'Préparer la demande', en: 'Prepare the application' }],
    status: { fr: 'Action prête', en: 'Action ready' },
  },
]

// Mini service progression shown inside the "Agit" card.
export const actFlow: Bi[] = [
  { fr: 'Préparer', en: 'Prepare' },
  { fr: 'Payer', en: 'Pay' },
  { fr: 'Suivre', en: 'Track' },
]

export const chatbotPoints: Bi[] = [
  { fr: 'Réponses génériques', en: 'Generic answers' },
  { fr: 'Aucun contexte organisationnel', en: 'No organizational context' },
  { fr: 'Peut halluciner', en: 'Can hallucinate' },
  { fr: 'Redemande les mêmes informations', en: 'Asks for the same information again' },
  { fr: 'N’exécute aucune action', en: 'No execution' },
]

export const atnaPoints: Bi[] = [
  { fr: 'Identité et contexte vérifiés', en: 'Verified identity and context' },
  { fr: 'Règles et données officielles', en: 'Official rules and data' },
  { fr: 'Réponses appuyées sur des sources et traçables', en: 'Sourced, traceable answers' },
  { fr: 'Recommandations personnalisées', en: 'Personalized recommendations' },
  { fr: 'Peut préparer et exécuter des actions autorisées', en: 'Can prepare and execute authorized actions' },
]

// DEMO / ILLUSTRATIVE UI DATA — not a published or measured ATNA accuracy metric.
export const tameDemo = { confidence: 92 }

export const tameSources: { key: SourceKey; label: Bi }[] = [
  { key: 'bylaw', label: { fr: 'Règlement municipal', en: 'Municipal by-law' } },
  { key: 'territory', label: { fr: 'Données territoriales', en: 'Territorial data' } },
  { key: 'internal', label: { fr: 'Base interne — permis', en: 'Internal database — permits' } },
  { key: 'official', label: { fr: 'Sources officielles', en: 'Official sources' } },
]
